import { isNumber } from 'es-toolkit/compat';
import { useTranslations } from 'next-intl';

import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';

import ProfitBoostImage from './assets/profit-boost.webp';

interface Props {
  durationsInMin: string;
  boostDailyRemainingCount?: number;
  boostDailyLimitCount?: number;
}

export const AboutProfitBoostContent = ({ durationsInMin, boostDailyRemainingCount, boostDailyLimitCount }: Props) => {
  const transPositionProfitBoost = useTranslations('Position_profit_boost.r250401');
  return (
    <div className={stack({ gap: 16 })}>
      <div>
        <img src={ProfitBoostImage.src} alt='profit boost' className={css({ w: '100%' })} />
      </div>
      <div
        className={hstack({
          alignItems: 'center',
          gap: 4,
          background: 'background.state.warning.tint',
          borderRadius: 8,
          p: 8,
        })}
      >
        <CautionCircleIcon size={16} className={css({ color: 'content.state.warning' })} />
        <span className={css({ color: 'content.state.warning', textStyle: 'Text/S/Regular' })}>
          {transPositionProfitBoost('Profit_rule_label')}
        </span>
      </div>
      <dl
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          borderRadius: 8,
          px: 8,

          '& > div': {
            display: 'flex',
            justifyContent: 'space-between',

            '& dt': {
              textStyle: 'Text/S/Strong',
              textAlign: 'left',
              color: 'content.default.level-2',
            },

            '& dd': {
              textStyle: 'Text/M/Regular',
              textAlign: 'right',
              color: 'content.default.level-5',
            },
          },
        })}
      >
        <div>
          <dt>{transPositionProfitBoost('Benefit')}</dt>
          <dd>{transPositionProfitBoost('Benefit_value')}</dd>
        </div>
        <div>
          <dt>{transPositionProfitBoost('Duration')}</dt>
          <dd>
            {transPositionProfitBoost('Duration_value_minute', { minute: durationsInMin })}{' '}
            <span className={css({ color: 'content.default.level-2' })}>
              {transPositionProfitBoost('Duration_value_after_payment')}
            </span>
          </dd>
        </div>
        {isNumber(boostDailyLimitCount) && isNumber(boostDailyRemainingCount) && (
          <div>
            <dt>{transPositionProfitBoost('Remaining_boost')}</dt>
            <dd>
              <span>{boostDailyRemainingCount}</span>
              <span className={css({ color: 'content.default.level-2' })}>/{boostDailyLimitCount}</span>
            </dd>
          </div>
        )}
      </dl>
      <details
        className={flex({
          flexDirection: 'column',
          textStyle: 'Text/M/Regular',
          px: 16,
          py: 12,
          backgroundColor: 'background.default.level-3',
          borderRadius: 8,
        })}
      >
        <summary
          className={flex({
            justify: 'space-between',
            textStyle: 'Text/M/Strong',
            color: 'content.default.level-5',
            align: 'center',
            cursor: 'pointer',
          })}
        >
          <span>{transPositionProfitBoost('About_profit_boost')}</span>
          <ChevronDownIcon
            size={20}
            className={css({
              color: 'content.default.level-2',
              '[open] &': {
                transform: 'rotate(-180deg)',
                transition: 'transform 0.2s',
              },
            })}
          />
        </summary>
        <div className={css({ mt: 12 })}>
          <ul
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-2',
              list: 'disc',
            })}
          >
            <li>{transPositionProfitBoost('About_description_1')}</li>
            <li>{transPositionProfitBoost('About_description_2')}</li>
            <li>{transPositionProfitBoost('About_description_3', { durationInMin: durationsInMin })}</li>
            <li>{transPositionProfitBoost('About_description_4')}</li>
            <li>{transPositionProfitBoost('About_description_5')}</li>
            <li>{transPositionProfitBoost('About_description_6')}</li>
            <li>{transPositionProfitBoost('About_description_7')}</li>
          </ul>
        </div>
      </details>
    </div>
  );
};
